import { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ImageUploader from './components/ImageUploader'
import GameBoard from './components/GameBoard'
import { GameState } from './types'

function App() {
  const [gameState, setGameState] = useState<GameState>({
    tiles: [],
    mergedGroups: [],
    gridSize: 3,
    imageLoaded: false,
    isComplete: false,
    imageAspectRatio: 1,
  })
  const [uploadedImage, setUploadedImage] = useState<HTMLImageElement | null>(null)
  const [restartKey, setRestartKey] = useState(0)

  const handleImageLoad = (imageElement: HTMLImageElement) => {
    // Image loaded - will trigger tile generation
    setUploadedImage(imageElement)
    const aspectRatio = imageElement.width / imageElement.height
    setGameState(prev => ({
      ...prev,
      imageLoaded: true,
      imageAspectRatio: aspectRatio,
    }))
  }

  const handleRestart = () => {
    // Reshuffle current puzzle - keep image and grid size
    setRestartKey(prev => prev + 1)
    setGameState(prev => ({
      ...prev,
      tiles: [],
      mergedGroups: [],
      isComplete: false,
    }))
  }

  const handleReset = () => {
    // Complete reset - new image
    setUploadedImage(null)
    setRestartKey(0)
    setGameState(prev => ({
      ...prev,
      tiles: [],
      mergedGroups: [],
      imageLoaded: false,
      isComplete: false,
      imageAspectRatio: 1,
    }))
  }

  const handleGridSizeChange = (size: number) => {
    setRestartKey(prev => prev + 1)
    setGameState(prev => ({
      ...prev,
      gridSize: size,
      tiles: [],
      mergedGroups: [],
      isComplete: false,
    }))
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} />
          <FormControl sx={{ minWidth: 120, mr: 2 }} size="small">
            <InputLabel sx={{ color: 'white' }}>Grid Size</InputLabel>
            <Select
              value={gameState.gridSize}
              label="Grid Size"
              onChange={(e) => handleGridSizeChange(Number(e.target.value))}
              sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'white' } }}
            >
              <MenuItem value={2}>2x2</MenuItem>
              <MenuItem value={3}>3x3</MenuItem>
              <MenuItem value={4}>4x4</MenuItem>
              <MenuItem value={5}>5x5</MenuItem>
            </Select>
          </FormControl>
          {gameState.imageLoaded && (
            <Button
              color="inherit"
              startIcon={<RestartAltIcon />}
              onClick={handleRestart}
              sx={{ mr: 1 }}
            >
              Restart
            </Button>
          )}
          <Button
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
          >
            New Game
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {!gameState.imageLoaded ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: 400,
              justifyContent: 'center',
              animation: 'fadeIn 0.5s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <ImageUploader onImageLoad={handleImageLoad} />
          </Paper>
        ) : (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              animation: 'fadeIn 0.5s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <GameBoard
              gameState={gameState}
              setGameState={setGameState}
              uploadedImage={uploadedImage}
              restartKey={restartKey}
            />
          </Paper>
        )}

        {gameState.isComplete && (
          <Paper
            elevation={3}
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'success.light',
              color: 'success.contrastText',
              animation: 'bounceIn 0.6s ease-out',
              '@keyframes bounceIn': {
                '0%': { opacity: 0, transform: 'scale(0.3)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { opacity: 1, transform: 'scale(1)' },
              },
            }}
          >
            <Typography variant="h6" align="center">
              Congratulations! You solved the puzzle!
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  )
}

export default App
